import React from 'react';

export default function HeroSection({ headline, subheadline, ctaText, onCtaClick, branding }) {
  const primaryColor = branding?.primary_color || '#1e40af';
  const accentColor = branding?.secondary_color || '#AEFFE3';

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 py-20 px-6 overflow-hidden flex items-center">
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 -right-40 w-96 h-96 rounded-full blur-3xl opacity-30 animate-pulse"
          style={{ backgroundColor: accentColor, animationDuration: '4s' }}
        />
        <div
          className="absolute bottom-0 -left-40 w-96 h-96 rounded-full blur-3xl opacity-20 animate-pulse"
          style={{ backgroundColor: primaryColor, animationDuration: '6s' }}
        />
        <div
          className="absolute top-1/2 left-1/2 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ backgroundColor: accentColor }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10 w-full">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Main content */}
          <div>
            <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
              <span className="text-sm font-semibold" style={{ color: accentColor }}>
                ✓ IRS-Compliant Strategies
              </span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6 text-white">
              {headline}
            </h1>

            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              {subheadline}
            </p>

            <button
              onClick={onCtaClick}
              className="group px-8 py-4 rounded-xl font-semibold text-lg text-white hover:scale-105 transition-all transform shadow-2xl relative overflow-hidden"
              style={{ backgroundColor: primaryColor }}
            >
              <span className="relative z-10">{ctaText}</span>
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ background: `linear-gradient(135deg, ${accentColor}40, transparent)` }}
              />
            </button>

            {/* Trust badges */}
            <div className="mt-8 flex flex-wrap gap-4 text-sm text-blue-200">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Zero Audit Risk</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>CPA Approved</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>100% Compliant</span>
              </div>
            </div>
          </div>

          {/* Right: Glass morphism stats card */}
          <div className="hidden md:block">
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
              <div className="space-y-6">
                <div className="pb-6 border-b border-white/20">
                  <div className="text-5xl font-bold mb-2" style={{ color: accentColor }}>
                    $50M+
                  </div>
                  <div className="text-white">Total Tax Savings Delivered</div>
                </div>

                <div className="pb-6 border-b border-white/20">
                  <div className="text-5xl font-bold mb-2" style={{ color: accentColor }}>
                    1,200+
                  </div>
                  <div className="text-white">Clients Protected</div>
                </div>

                <div>
                  <div className="text-5xl font-bold mb-2" style={{ color: accentColor }}>
                    98%
                  </div>
                  <div className="text-white">Client Satisfaction Rate</div>
                </div>
              </div>

              {/* Mini testimonial */}
              <div className="mt-8 pt-8 border-t border-white/20">
                <div className="flex items-start gap-3">
                  <svg className="w-8 h-8 text-white/40 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                  <div>
                    <p className="text-white/90 italic text-sm mb-2">
                      "Saved us $200K in two years. Zero audit risk."
                    </p>
                    <div className="text-xs text-white/60">
                      Jennifer Torres, CFO
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
